import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, message, Space, Tag, DatePicker, Card, Switch } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import './index.css';

const { RangePicker } = DatePicker;

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();
  const [searchParams, setSearchParams] = useState({});
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [batchDeleteLoading, setBatchDeleteLoading] = useState(false);


  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async (params = {}) => {
    setLoading(true);
    try {
      const response = await axios.get('/api/users', { params });
      setUsers(response.data);
    } catch (error) {
      message.error('获取用户列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 搜索处理
  const handleSearch = (values) => {
    const params = { ...values };

    // 处理日期范围
    if (values.created_at && values.created_at.length === 2) {
      params.created_at_start = values.created_at[0].format('YYYY-MM-DD');
      params.created_at_end = values.created_at[1].format('YYYY-MM-DD');
      delete params.created_at;
    }

    // 过滤空值
    Object.keys(params).forEach(key => {
      if (params[key] === undefined || params[key] === null || params[key] === '') {
        delete params[key];
      }
    });

    setSearchParams(params);
    fetchUsers(params);
  };

  // 重置搜索
  const handleReset = () => {
    searchForm.resetFields();
    setSearchParams({});
    fetchUsers();
  };

  const handleAdd = () => {
    setEditingUser(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingUser(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这个用户吗？',
      onOk: async () => {
        try {
          await axios.delete(`/api/users/${id}`);
          message.success('删除成功');
          fetchUsers(searchParams); // 保持当前搜索条件
        } catch (error) {
          message.error('删除失败');
        }
      }
    });
  };

  const handleSubmit = async (values) => {
    try {
      if (editingUser) {
        await axios.put(`/api/users/${editingUser.id}`, values);
        message.success('更新成功');
      } else {
        await axios.post('/api/users', values);
        message.success('创建成功');
      }
      setModalVisible(false);
      fetchUsers(searchParams); // 保持当前搜索条件
    } catch (error) {
      const errorMessage = error.response?.data?.message || (editingUser ? '更新失败' : '创建失败');
      message.error(errorMessage);
    }
  };




  // 批量删除处理
  const handleBatchDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要删除的用户');
      return;
    }

    Modal.confirm({
      title: '确认批量删除',
      content: `确定要删除选中的 ${selectedRowKeys.length} 个用户吗？此操作不可恢复。`,
      okText: '确认删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        setBatchDeleteLoading(true);
        try {
          const response = await axios.post('/api/users/batch-delete', {
            user_ids: selectedRowKeys
          });
          message.success(response.data.message);
          setSelectedRowKeys([]);
          setSelectedRows([]);
          fetchUsers(searchParams);
        } catch (error) {
          const errorMessage = error.response?.data?.message || '批量删除失败';
          message.error(errorMessage);
        } finally {
          setBatchDeleteLoading(false);
        }
      }
    });
  };

  // 行选择配置
  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedRowKeys(selectedRowKeys);
      setSelectedRows(selectedRows);
    },
    getCheckboxProps: (record) => ({
      // 禁用admin账号的选择
      disabled: record.username === 'admin',
    }),
  };


  // 状态开关处理
  const handleStatusChange = async (userId, checked) => {
    const newStatus = checked ? 'active' : 'inactive';

    try {
      const response = await axios.put(`/api/users/${userId}/status`, {
        status: newStatus
      });
      message.success(response.data.message || '状态更新成功');
      fetchUsers(searchParams); // 刷新列表
    } catch (error) {
      const errorMessage = error.response?.data?.message || '状态更新失败';
      message.error(errorMessage);
      // 如果更新失败，需要刷新列表以恢复正确的状态
      fetchUsers(searchParams);
    }
  };

  const getRoleColor = (role) => {
    const roleMap = {
      admin: 'red',
      user: 'blue',
      master: 'green',
      service: 'orange'
    };
    return roleMap[role] || 'default';
  };

  const getRoleText = (role) => {
    const roleMap = {
      admin: '管理员',
      user: '用户',
      master: '师傅',
      service: '客服'
    };
    return roleMap[role] || role;
  };

  const getStatusColor = (status) => {
    const statusMap = {
      active: 'green',
      inactive: 'orange',
      banned: 'red'
    };
    return statusMap[status] || 'default';
  };

  const getStatusText = (status) => {
    const statusMap = {
      active: '活跃',
      inactive: '非活跃',
      banned: '封禁'
    };
    return statusMap[status] || status;
  };

  const columns = [
    {
      title: '用户编号',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '用户名称',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: '用户昵称',
      dataIndex: 'nickname',
      key: 'nickname',
    },
    {
      title: '手机号码',
      dataIndex: 'mobile',
      key: 'mobile',
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: '性别',
      dataIndex: 'gender',
      key: 'gender',
      render: (gender) => {
        const genderMap = {
          male: '男',
          female: '女',
          '': '未设置'
        };
        return genderMap[gender] || '未设置';
      }
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      render: (role) => (
        <Tag color={getRoleColor(role)}>
          {getRoleText(role)}
        </Tag>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status, record) => {
        // 如果是admin用户，禁用开关
        if (record.username === 'admin') {
          return <Tag color="red">超级管理员</Tag>;
        }

        return (
          <Switch
            checked={status === 'active'}
            onChange={(checked) => handleStatusChange(record.id, checked)}
            checkedChildren="活跃"
            unCheckedChildren="禁用"
            size="small"
          />
        );
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => dayjs(date).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          {record.username === 'admin' ? null : (
            <>
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
                className="edit-btn"
              >
                编辑
              </Button>
              <Button
                type="link"
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record.id)}
                className="delete-btn"
                disabled={record.username === 'admin'}
              >
                删除
              </Button>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="users-container">


      {/* 搜索区域 */}
      <Card className="search-card">
        <h3 className="search-title">用户管理</h3>
        <Form
          form={searchForm}
          layout="vertical"
          onFinish={handleSearch}
          className="search-form"
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            <Form.Item name="username" label="用户名">
              <Input placeholder="请输入用户名" />
            </Form.Item>

            <Form.Item name="mobile" label="手机号码">
              <Input placeholder="请输入手机号码" />
            </Form.Item>

            <Form.Item name="status" label="状态">
              <Select placeholder="请选择状态" allowClear>
                <Select.Option value="active">活跃</Select.Option>
                <Select.Option value="inactive">禁用</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item name="created_at" label="创建时间">
              <RangePicker
                placeholder={['开始日期', '结束日期']}
                format="YYYY-MM-DD"
              />
            </Form.Item>
          </div>

          {/* 搜索按钮区域 */}
          <div className="search-actions">
            <Button
              type="primary"
              htmlType="submit"
              icon={<SearchOutlined />}
              className="search-btn"
            >
              搜索
            </Button>
            <Button
              onClick={handleReset}
              icon={<ReloadOutlined />}
              className="reset-btn"
            >
              重置
            </Button>
          </div>
        </Form>
      </Card>


      <div className="table-container">
        <div className="table-bar">
          <div className="table-bar-left">
            <span className="selected-info">
              已选择 {selectedRowKeys.length} 项
            </span>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
            className="add-btn"
          >
            新增用户
          </Button>
          {/* 批量删除按钮 */}
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={handleBatchDelete}
            className="batch-delete-btn"
            disabled={selectedRowKeys.length === 0}
            loading={batchDeleteLoading}
          >
            批量删除
          </Button>


        </div>

        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          className="users-table"
          rowSelection={rowSelection}
          size='small'
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
        />
      </div>

      <Modal
        title={editingUser ? '编辑用户' : '新增用户'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        className="user-modal"
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          className="user-form"
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
            <Form.Item
              name="nickname"
              label="用户昵称"
              rules={[{ required: true, message: '请输入用户昵称' }]}
            >
              <Input placeholder="请输入用户昵称" />
            </Form.Item>

            <Form.Item
              name="mobile"
              label="手机号码"
            >
              <Input placeholder="请输入手机号码" />
            </Form.Item>

            <Form.Item
              name="email"
              label="邮箱"
            >
              <Input placeholder="请输入邮箱" />
            </Form.Item>

            <Form.Item
              name="username"
              label="用户名称"
              rules={[{ required: true, message: '请输入用户名称' }]}
            >
              <Input placeholder="请输入用户名称" />
            </Form.Item>

            {!editingUser && (
              <Form.Item
                name="password"
                label="用户密码"
                rules={[{ required: true, message: '请输入用户密码' }]}
              >
                <Input.Password placeholder="请输入用户密码" />
              </Form.Item>
            )}

            <Form.Item
              name="gender"
              label="用户性别"
            >
              <Select placeholder="请选择性别">
                <Select.Option value="male">男</Select.Option>
                <Select.Option value="female">女</Select.Option>
                <Select.Option value="">未设置</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="role"
              label="角色"
              rules={[{ required: true, message: '请选择角色' }]}
            >
              <Select placeholder="请选择角色">
                <Select.Option value="master">师傅</Select.Option>
                <Select.Option value="service">客服</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="status"
              label="状态"
              rules={[{ required: true, message: '请选择状态' }]}
            >
              <Select placeholder="请选择状态">
                <Select.Option value="active">激活</Select.Option>
                <Select.Option value="inactive">禁用</Select.Option>
                {/* <Select.Option value="banned">封禁</Select.Option> */}
              </Select>
            </Form.Item>
          </div>

          <Form.Item className="form-actions" style={{ marginTop: '24px', textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                取消
              </Button>
              <Button type="primary" htmlType="submit" className="submit-btn">
                {editingUser ? '保存' : '提交'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Users;